/**
 * useOkrs (CHAP-417) — write mutations against the CHAP-378 OKR API.
 *
 * OKRs are READ as part of the hydrated tenant payload (`useTenantData` ->
 * `/api/c/:slug/tenant-config`), not from a dedicated list endpoint, so every
 * mutation here invalidates the `['tenant-config', slug]` key rather than an
 * `['okrs', slug]` key that nothing populates.
 *
 * Refetch-after-write, not optimistic — matching `useVotes.ts`. That is a
 * deliberate choice here beyond mere consistency: with a second, unsynced copy
 * of these OKRs live in the legacy board dashboard's D1 (see
 * `lib/okr-provenance.ts`), an optimistic cache would let the page display a
 * value that never round-tripped through the store it claims to be showing.
 * The server response is the only thing that proves what Postgres holds.
 *
 * Every route below is gated server-side by `requireAuth + requireTier('cabinet')`;
 * a sub-cabinet caller gets 403 and `ApiError` surfaces it. The UI hides the
 * affordances too (`<TierGuard tier="cabinet">`), but the server gate is the
 * real one.
 */
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { apiDelete, apiPatch, apiPost } from '../lib/api.js';
import type { OkrInitiative, OkrKeyResult } from '../../shared/types.js';

/** Initiative fields a cabinet member may author. */
export interface InitiativeInput {
  name?: string;
  phase?: string | null;
  status?: string | null;
  targetProgress?: number | null;
  currentProgress?: number;
  sortOrder?: number;
}

/** Key-result fields a cabinet member may author. */
export interface KeyResultInput {
  name?: string;
  target?: string | number | null;
  current?: string | number | null;
  unit?: string | null;
  ownerUserId?: string | null;
  dueDate?: string | null;
  note?: string | null;
  sortOrder?: number;
}

type InitiativeResponse = { ok: boolean; initiative: OkrInitiative };
type KeyResultResponse = { ok: boolean; keyResult: OkrKeyResult };

/**
 * Shared invalidation. OKR writes can move rows the tenant payload derives
 * other cards from, so the whole tenant-config key is refetched rather than
 * surgically patched.
 */
function useOkrInvalidation(slug: string) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['tenant-config', slug] });
}

export function useCreateInitiative(
  slug: string,
): UseMutationResult<InitiativeResponse, Error, InitiativeInput & { name: string }> {
  const invalidate = useOkrInvalidation(slug);
  return useMutation({
    mutationFn: (body) => apiPost<InitiativeResponse>(`/api/c/${slug}/okrs/initiatives`, body),
    onSuccess: invalidate,
  });
}

export function useUpdateInitiative(
  slug: string,
): UseMutationResult<InitiativeResponse, Error, { id: string; patch: InitiativeInput }> {
  const invalidate = useOkrInvalidation(slug);
  return useMutation({
    mutationFn: ({ id, patch }) =>
      apiPatch<InitiativeResponse>(`/api/c/${slug}/okrs/initiatives/${id}`, patch),
    onSuccess: invalidate,
  });
}

/**
 * Deleting an initiative CASCADEs its key results, so the server refuses with
 * 409 `cascade_confirmation_required` unless `cascade` is set. The caller is
 * expected to surface that refusal to the user and re-issue with
 * `cascade: true` only on an explicit confirmation — never to pass `true`
 * unconditionally, which would defeat the confirmation the API deliberately
 * forces.
 */
export function useDeleteInitiative(
  slug: string,
): UseMutationResult<
  { ok: boolean; deleted: { initiativeId: string; keyResultsDeleted: number } },
  Error,
  { id: string; cascade?: boolean }
> {
  const invalidate = useOkrInvalidation(slug);
  return useMutation({
    mutationFn: ({ id, cascade }) =>
      apiDelete(`/api/c/${slug}/okrs/initiatives/${id}${cascade ? '?cascade=true' : ''}`),
    onSuccess: invalidate,
  });
}

export function useCreateKeyResult(
  slug: string,
): UseMutationResult<KeyResultResponse, Error, KeyResultInput & { initiativeId: string; name: string }> {
  const invalidate = useOkrInvalidation(slug);
  return useMutation({
    mutationFn: (body) => apiPost<KeyResultResponse>(`/api/c/${slug}/okrs/key-results`, body),
    onSuccess: invalidate,
  });
}

export function useUpdateKeyResult(
  slug: string,
): UseMutationResult<KeyResultResponse, Error, { id: string; patch: KeyResultInput }> {
  const invalidate = useOkrInvalidation(slug);
  return useMutation({
    mutationFn: ({ id, patch }) =>
      apiPatch<KeyResultResponse>(`/api/c/${slug}/okrs/key-results/${id}`, patch),
    onSuccess: invalidate,
  });
}

export function useDeleteKeyResult(
  slug: string,
): UseMutationResult<{ ok: boolean; deleted: { keyResultId: string } }, Error, { id: string }> {
  const invalidate = useOkrInvalidation(slug);
  return useMutation({
    mutationFn: ({ id }) => apiDelete(`/api/c/${slug}/okrs/key-results/${id}`),
    onSuccess: invalidate,
  });
}
